import { AccountPlatformType } from "../../../shared/types/accounts";
import { getAccountPlatformType } from "./account-platform";

/**
 * When a connected account row and an identity offered by a platform are the
 * same account. Mirrors the server's `utils/accountIdentity.ts`, and the two
 * specs share their cases.
 *
 * Same platform, then either a platform id matches, or, on handle platforms
 * only, the username matches case-insensitively. Facebook, and every platform
 * not listed as a handle platform, matches by id only.
 *
 * The server also requires the same admin. Every account in the client store
 * belongs to the current admin, so there is no admin comparison here.
 */

export type AccountIdentityMatch = "id" | "username";

export type IdentityValue = string | number | null | undefined;

export interface AccountIdentityRow {
  platformType?: string | null;
  login?: string | null;
  platformId?: unknown;
  platformPageId?: unknown;
  instagram_business_account?: { id?: unknown } | null;
  igData?: object | null;
  defaultPage?: { id?: unknown } | null;
}

export interface AccountIdentityCandidate {
  platformType: string | null | undefined;
  ids: readonly IdentityValue[];
  username?: string | null;
}

const HANDLE_PLATFORMS = new Set(["IG", "TT", "TH", "TW"]);

const STORED_ID_FIELDS: Record<string, readonly string[]> = {
  IG: ["platformId", "instagram_business_account.id", "igData.id"],
  FB: ["platformId", "platformPageId", "defaultPage.id"],
};

const DEFAULT_STORED_ID_FIELDS: readonly string[] = ["platformId"];

export function identityPlatformOf(
  platformType: string | null | undefined
): string {
  return getAccountPlatformType({
    platformType: (platformType || undefined) as AccountPlatformType,
  });
}

export function storedPlatformIdFields(
  platformType: string | null | undefined
): readonly string[] {
  return (
    STORED_ID_FIELDS[identityPlatformOf(platformType)] ??
    DEFAULT_STORED_ID_FIELDS
  );
}

function normaliseId(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const id = String(value).trim();
  return id || null;
}

function normaliseUsername(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const username = value.trim().toLowerCase();
  return username || null;
}

function readPath(row: unknown, path: string): unknown {
  return path
    .split(".")
    .reduce<unknown>(
      (value, key) =>
        value === null || value === undefined
          ? undefined
          : (value as Record<string, unknown>)[key],
      row
    );
}

export function storedPlatformIds(row: AccountIdentityRow): string[] {
  const ids = storedPlatformIdFields(row.platformType)
    .map((field) => normaliseId(readPath(row, field)))
    .filter((id): id is string => id !== null);
  return [...new Set(ids)];
}

export function matchAccountIdentity(
  row: AccountIdentityRow,
  candidate: AccountIdentityCandidate
): AccountIdentityMatch | null {
  const platform = identityPlatformOf(row.platformType);
  if (platform !== identityPlatformOf(candidate.platformType)) return null;

  const stored = storedPlatformIds(row);
  const matchesId = candidate.ids.some((value) => {
    const id = normaliseId(value);
    return id !== null && stored.includes(id);
  });
  if (matchesId) return "id";

  if (!HANDLE_PLATFORMS.has(platform)) return null;
  const rowUsername = normaliseUsername(row.login);
  if (rowUsername && rowUsername === normaliseUsername(candidate.username)) {
    return "username";
  }
  return null;
}

/** The row matching by id, else the row matching by username, else undefined. */
export function findAccountByIdentity<Row extends AccountIdentityRow>(
  rows: readonly Row[],
  candidate: AccountIdentityCandidate
): Row | undefined {
  const matches = rows.map((row) => matchAccountIdentity(row, candidate));
  const index = matches.includes("id")
    ? matches.indexOf("id")
    : matches.indexOf("username");
  return index === -1 ? undefined : rows[index];
}

/** The candidate for an Instagram profile Facebook Graph returned. */
export function instagramProfileCandidate(ig: {
  id?: IdentityValue;
  ig_id?: IdentityValue;
  username?: string | null;
}): AccountIdentityCandidate {
  return { platformType: "IG", ids: [ig.id, ig.ig_id], username: ig.username };
}
